<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <slot :generate-report="generateReport"></slot>
    <div v-if="headers.length !== 0" class="orangehrm-paper-container">
      <oxd-report-table
        :items="items"
        :headers="headers"
        :loading="isLoading"
        :column-count="colCount"
      >
        <template #pagination>
          <oxd-text class="oxd-text--count" tag="span">
            {{ $t('general.n_records_found', {count: total}) }}
          </oxd-text>
          <oxd-pagination
            v-if="showPaginator"
            v-model:current="currentPage"
            :length="pages"
          />
        </template>
        <template #footer>
          <div class="orangehrm-report-table-footer">
            <oxd-text v-if="$slots.footer" class="oxd-text--footer" tag="span">
              <slot name="footer" :data="response"></slot>
            </oxd-text>
            <oxd-button
              v-if="enableExport && canExport"
              class="orangehrm-report-export-button"
              type="button"
              display-type="secondary"
              :label="$t('general.export_to_excel')"
              :loading="isExporting"
              @click="exportToExcel"
            />
          </div>
        </template>
      </oxd-report-table>
    </div>
  </div>
</template>

<script>
import {computed, onBeforeMount, ref, watch} from 'vue';
import ExcelJS from 'exceljs';
import {saveAs} from 'file-saver';
import {APIService} from '@/core/util/services/api.service';
import {navigate} from '@ohrm/core/util/helper/navigation';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {CellAdapter, OxdMultilineCell, OxdReportTable} from '@ohrm/oxd';

export default {
  name: 'ReportsTable',

  components: {
    'oxd-report-table': OxdReportTable,
  },

  props: {
    name: {
      type: String,
      required: true,
    },
    module: {
      type: String,
      required: true,
    },
    prefetch: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    columnCount: {
      type: Number,
      default: null,
      required: false,
    },
    excludePageParams: {
      type: Boolean,
      default: false,
    },
    reportsResource: {
      type: String,
      default: 'reports',
    },
    enableExport: {
      type: Boolean,
      default: false,
    },
    exportFileName: {
      type: String,
      default: '',
    },
    excelBuilder: {
      type: Function,
      default: null,
    },
  },

  setup(props) {
    const reportsBasePath = computed(
      () => `/api/v2/${props.module}/${props.reportsResource}`,
    );

    const http = new APIService(
      window.appGlobal.baseUrl,
      `${reportsBasePath.value}/data`,
    );

    const headers = ref([]);
    const colCount = ref(props.columnCount ? props.columnCount : 0);
    const isExporting = ref(false);
    const serializedFilters = computed(() => {
      if (!props.excludePageParams) {
        return {
          ...props.filters,
          name: props.name,
          _dateFormattingEnabled: true,
        };
      }

      return {
        ...props.filters,
        name: props.name,
      };
    });

    const {
      total,
      pages,
      response,
      isLoading,
      currentPage,
      showPaginator,
      execQuery: fetchTableData,
    } = usePaginate(http, {
      query: serializedFilters,
      prefetch: false,
    });

    const items = computed(() => {
      const _items = Array.isArray(response.value.data)
        ? response.value.data
        : [];
      return _items.map((item) => {
        let _rows = 0;
        for (const key in item) {
          const value = item[key];
          if (Array.isArray(value) && value.length > _rows)
            _rows = value.length;
        }
        return {...item, _rows};
      });
    });

    const setupTableHeaders = (header) => {
      delete header['size'];
      const {type, ...rest} = header.cellProperties ?? {};
      const cellProperties = function ({prop, model}) {
        const url = model?._url ? model?._url[prop] : undefined;
        return {
          ...rest,
          onClick: url ? () => navigate(url) : undefined,
        };
      };
      return {
        ...header,
        cellProperties,
        cellTemplate:
          type === 'list' ? CellAdapter(OxdMultilineCell) : undefined,
      };
    };

    const fetchTableHeaders = async () => {
      isLoading.value = true;
      const {name, ...filterParams} = serializedFilters.value;
      http
        .request({
          method: 'GET',
          url: reportsBasePath.value,
          params: {
            name,
            ...filterParams,
            ...(serializedFilters.value?.reportId && {
              reportId: serializedFilters.value.reportId,
            }),
          },
        })
        .then((response) => {
          const {data, meta} = response.data;
          headers.value = data.headers.map((header) => {
            if (header?.children && Array.isArray(header.children)) {
              header.children = header.children.map((child) =>
                setupTableHeaders(child),
              );
              return header;
            } else {
              return setupTableHeaders(header);
            }
          });
          if (meta.headers?.columnCount) {
            colCount.value = meta.headers.columnCount;
          }
          isLoading.value = false;
        });
    };

    const generateReport = async () => {
      await fetchTableHeaders();
      if (!props.excludePageParams) {
        await fetchTableData();
      } else {
        await fetchTableData(false);
      }
    };

    const flattenHeaders = (headerList) => {
      const result = [];
      headerList.forEach((header) => {
        if (header?.children?.length) {
          header.children.forEach((child) => result.push(child));
        } else {
          result.push(header);
        }
      });
      return result;
    };

    const formatExportCellValue = (value) => {
      if (value === null || value === undefined) {
        return '';
      }
      if (Array.isArray(value)) {
        return value.join('\n');
      }
      if (typeof value === 'number') {
        return value;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (/^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(trimmed)) {
          const numeric = Number(trimmed.replace(/,/g, ''));
          if (!Number.isNaN(numeric)) {
            return numeric;
          }
        }
      }
      return value;
    };

    const resolveExportFileName = () => {
      if (props.exportFileName) {
        return props.exportFileName;
      }
      const year = props.filters?.year;
      const month = props.filters?.month;
      if (year && month) {
        return `Monthly-Payroll-Report-${year}-${String(month).padStart(
          2,
          '0',
        )}`;
      }

      return 'report';
    };

    const canExport = computed(
      () =>
        props.enableExport &&
        headers.value.length > 0 &&
        items.value.length > 0 &&
        !isLoading.value,
    );

    const fitWorksheetColumns = (worksheet, columnCount, rowCount) => {
      for (let columnIndex = 1; columnIndex <= columnCount; columnIndex++) {
        let maxLength = 0;

        for (let rowIndex = 1; rowIndex <= rowCount; rowIndex++) {
          const cell = worksheet.getRow(rowIndex).getCell(columnIndex);
          const value = cell.value;
          if (value === null || value === undefined) {
            continue;
          }

          const text = String(value);
          const displayLength =
            rowIndex === 1 ? Math.ceil(text.length * 1.15) : text.length;
          maxLength = Math.max(maxLength, displayLength);
        }

        worksheet.getColumn(columnIndex).width = Math.min(
          60,
          Math.max(10, maxLength + 2),
        );
      }
    };

    const exportToExcel = async () => {
      if (!canExport.value || isExporting.value) {
        return;
      }

      isExporting.value = true;
      try {
        const workbook = new ExcelJS.Workbook();
        if (typeof props.excelBuilder === 'function') {
          const worksheet = workbook.addWorksheet('Sheet1');
          props.excelBuilder(worksheet, items.value);
        } else {
          const flatHeaders = flattenHeaders(headers.value);
          const exportRows = items.value.map((item) =>
            flatHeaders.map((header) =>
              formatExportCellValue(item[header.prop]),
            ),
          );
          const worksheet = workbook.addWorksheet('Report');

          worksheet.addRow(
            flatHeaders.map((header) => header.name ?? header.prop),
          );
          exportRows.forEach((rowValues) => {
            worksheet.addRow(rowValues);
          });

          worksheet.getRow(1).font = {bold: true};
          fitWorksheetColumns(
            worksheet,
            flatHeaders.length,
            exportRows.length + 1,
          );
        }

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${resolveExportFileName()}.xlsx`);
      } finally {
        isExporting.value = false;
      }
    };

    watch(
      () => props.name,
      () => {
        headers.value = [];
      },
    );

    props.prefetch && onBeforeMount(() => generateReport());

    return {
      total,
      pages,
      items,
      headers,
      colCount,
      response,
      isLoading,
      currentPage,
      showPaginator,
      generateReport,
      exportToExcel,
      canExport,
      isExporting,
    };
  },
};
</script>

<style src="./reports-table.scss" lang="scss" scoped></style>
